import _ from "lodash";
import { useSelector } from "react-redux";
import { store } from "redux/store";

export const getCurrentPlan = () => {
  const subscriptionData = store?.getState()?.subscription;
  const availablePlans = subscriptionData?.availablePlans;
  const subscription = subscriptionData?.currentSubscription?.subscriptions;
  const currentPlan =
    availablePlans
      ?.find((f) => f?.id === subscription?.[0]?.obj_data?.plan?.product)
      ?.name.toUpperCase() || "TRIAL";
  return currentPlan;
};

export const getPlanStatus = (subscriptions = []) => {
  if (_.isEmpty(subscriptions)) return null;
  const latestSubscription = subscriptions?.reduce((latest, current) => {
    return current?.obj_data?.created > latest?.obj_data?.created
      ? current
      : latest;
  }, subscriptions?.[0]);
  return latestSubscription?.obj_data?.status;
};

export const isSubscriptionExpired = () => {
  const currentPlan = getCurrentPlan();

  if (currentPlan === "TRIAL") return getProTrialStatus().isTrialExpired;

  const isExpired = store?.getState()?.planUsage?.isExpired;

  return isExpired || false;
};

export const activeStatus = ["trialing", "past_due", "active", "paused"];

export const getProTrialStatus = () => {
  const subscriptionData = store?.getState()?.subscription;
  const subscription =
    subscriptionData?.currentSubscription?.subscriptions?.[0]?.obj_data;

  if (!subscription)
    return {
      isTrialing: false,
      isTrialExpired: false,
      trialEndDate: null,
      isProTrial: false,
    };

  const { status, trial_end, cancel_at, canceled_at, license_type, trial_end_date } = subscription;

  // Our 15-day trial is a `trial_subscriptions` record (license_type: "trial",
  // trial_end_date as an ISO string) — not a Stripe subscription with a native
  // trialing status, which is what trial_end/status below are for.
  const isCustomTrial = license_type === "trial";
  const now = new Date();
  const trialEndTimestamp = isCustomTrial
    ? (trial_end_date ? new Date(trial_end_date) : null)
    : (trial_end ? new Date(trial_end * 1000) : null);

  const isTrialing = isCustomTrial
    ? Boolean(trialEndTimestamp && trialEndTimestamp > now)
    : status === "trialing" && trialEndTimestamp && trialEndTimestamp > now;

  const isTrialExpired = isCustomTrial
    ? Boolean(trialEndTimestamp && trialEndTimestamp <= now)
    : status === "paused" &&
      trialEndTimestamp &&
      trialEndTimestamp < now &&
      !cancel_at &&
      !canceled_at;

  const isProTrial = isCustomTrial || status === "trialing" || isTrialExpired;

  return {
    isTrialing,
    isTrialExpired,
    trialEndDate: trialEndTimestamp,
    status,
    isProTrial,
  };
};

export function getSubscriptionPlanButtonClass({
  plan,
  priceData,
  currentSubscription,
  currentPlanStatus,
  activeStatus,
}) {
  const { isProTrial, isTrialExpired } = getProTrialStatus();
  const currentSubscriptionPlanId =
    currentSubscription?.subscriptions?.[0]?.obj_data?.plan?.id;
  const subscriptionStatus =
    currentSubscription?.subscriptions?.[0]?.obj_data?.status;

  if (isProTrial && isTrialExpired && plan?.code !== "enterprise") {
    return "bg-primary text-light";
  }

  if (
    !_.isEmpty(currentSubscription?.subscriptions) &&
    currentSubscriptionPlanId === priceData?.obj_data?.id
  ) {
    if (subscriptionStatus === "past_due") {
      return "bg-primary text-light";
    }

    return activeStatus?.includes(currentPlanStatus)
      ? "bg-success text-light"
      : "bg-black text-light";
  }

  return "bg-primary text-light";
}

export function getPlanButtonLabel({
  plan,
  priceData,
  currentSubscription,
  currentPlanStatus,
  activeStatus,
  translationKey,
}) {
  const { isProTrial, isTrialExpired } = getProTrialStatus();
  const currentSubscriptionPlanId =
    currentSubscription?.subscriptions?.[0]?.obj_data?.plan?.id;
  const subscriptionStatus =
    currentSubscription?.subscriptions?.[0]?.obj_data?.status;

  if (isProTrial && isTrialExpired && plan?.code !== "enterprise") {
    return translationKey("store.START_SUBSCRIPTION");
  }

  if (
    !_.isEmpty(currentSubscription?.subscriptions) &&
    currentSubscriptionPlanId === priceData?.obj_data?.id
  ) {
    if (subscriptionStatus === "past_due") {
      return translationKey("store.UPDATE_PAYMENT");
    }

    return activeStatus?.includes(currentPlanStatus)
      ? translationKey("store.MANAGE_SUBSCRIPTION")
      : translationKey("store.RENEW_SUBSCRIPTION");
  }

  return plan?.code !== "enterprise"
    ? translationKey("common.CHANGE_PLAN")
    : translationKey("store.CONTACT_SALES");
}

export const getSubscriptionStatusInfo = () => {
  const subscriptionData = store?.getState()?.subscription;
  const subscription =
    subscriptionData?.currentSubscription?.subscriptions?.[0]?.obj_data;
  const currentPlan = getCurrentPlan();
  const { isProTrial, isTrialExpired, trialEndDate } = getProTrialStatus();
  const isRegularExpired = isSubscriptionExpired();

  const statusInfo = {
    hasIssue: false,
    statusType: null,
    messageKey: null,
    dateKey: null,
    date: null,
    iconType: null,
    actionRequired: false,
  };

  if (subscription?.status === "past_due") {
    const currentPeriodStart = subscription?.current_period_start;
    return {
      hasIssue: true,
      statusType: "payment_failed",
      messageKey: "license_messages.PAYMENT_FAILED_MESSAGE",
      dateKey: "license_messages.PAYMENT_FAILED_SINCE",
      date: currentPeriodStart ? new Date(currentPeriodStart * 1000) : null,
      iconType: "error",
      actionRequired: true,
    };
  }

  if (isProTrial && isTrialExpired) {
    return {
      hasIssue: true,
      statusType: "trial_expired",
      messageKey: "welcome_pro_trial.PRO_TRIAL_EXPIRED_ON",
      dateKey: null,
      date: trialEndDate,
      iconType: "error",
      actionRequired: true,
    };
  }

  if (isRegularExpired && currentPlan.toLowerCase() !== "trial") {
    const canceledAt = subscription?.canceled_at || subscription?.cancel_at;
    return {
      hasIssue: true,
      statusType: "subscription_expired",
      messageKey: "license_messages.SUBSCRIPTION_EXPIRED_ON",
      dateKey: null,
      date: canceledAt ? new Date(canceledAt * 1000) : null,
      iconType: "error",
      actionRequired: true,
    };
  }
  if (isProTrial && !isTrialExpired) {
    return {
      hasIssue: false,
      statusType: "trial_active",
      messageKey: "welcome_pro_trial.TRIAL_ENDS_ON",
      dateKey: null,
      date: trialEndDate,
      iconType: "info",
      actionRequired: false,
    };
  }

  if (
    subscription?.current_period_end &&
    currentPlan.toLowerCase() !== "trial"
  ) {
    return {
      hasIssue: false,
      statusType: "subscription_active",
      messageKey: "license_messages.SUBSCRIPTION_ENDS_ON",
      dateKey: null,
      date: new Date(subscription.current_period_end * 1000),
      iconType: "info",
      actionRequired: false,
    };
  }

  return statusInfo;
};

export const getPlanButtonText = (i18n, licenseObj, currentPlanStatus) => {
  const { isProTrial, isTrialExpired, trialEndDate } = getProTrialStatus();
  const isProExpired = isSubscriptionExpired();
  const currentPlan = getCurrentPlan();

  const getTrialDaysRemaining = () => {
    if (!trialEndDate) return null;
    const today = new Date();
    const endDate = new Date(trialEndDate);
    const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    return daysRemaining >= 0 ? daysRemaining : 0;
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  if (isProTrial) {
    if (isTrialExpired) {
      return i18n.t("store.TRIAL_EXPIRED");
    }
    return trialDaysRemaining !== null
      ? `${i18n.t("store.Trial")} - ${trialDaysRemaining}d`
      : i18n.t("store.Pro");
  }

  if (currentPlanStatus === "past_due") {
    return i18n.t("store.TRIAL_EXPIRED");
  }

  if (currentPlan.toLowerCase() === "pro" && !isProTrial) {
    if (isProExpired) {
      return i18n.t("store.PRO_EXPIRED");
    }
    return i18n.t("store.Pro");
  }

  return i18n.t(`store.${_.capitalize(licenseObj?.license || currentPlan)}`);
};
